import itLocale from 'date-fns/locale/it';
import enLocale from 'date-fns/locale/en-US';
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  startOfMonth,
  endOfMonth,
  addDays as addDaysFns,
  subDays,
  isSameDay,
  differenceInCalendarDays,
} from 'date-fns';
import {
  itIT as itPickers,
  enUS as enPickers,
} from '@mui/x-date-pickers/locales';
import { getLocale } from '../i18n';
/**Js file containing all usable Dates functions **/

const TODAY = new Date();
const DAY_IN_MS = 24 * 60 * 60 * 1000; //24 hours in milliseconds

// Dates follow the language the app speaks, not the browser's: an English browser on the Italian
// interface still reads 24/09/2026.
const getLang = () => (getLocale() === 'en' ? 'en-US' : 'it-IT');

export const culture = getLang();
export const locales = {
  'it-IT': itLocale,
  'en-US': enLocale,
};
// What a date picker's LocalizationProvider needs to name days, months and its own buttons in the
// app's language.
export const pickerLocaleProps = {
  adapterLocale: locales[culture],
  localeText: (culture === 'it-IT' ? itPickers : enPickers).components
    .MuiLocalizationProvider.defaultProps.localeText,
};
export const options = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

//compares 2 provided dates
//useful for conditions
export const compareDates = (date1, date2, compareOperator = 'equal') => {
  if (compareOperator === 'bigger') return date1.getTime() > date2.getTime();

  if (compareOperator === 'smaller') return date1.getTime() < date2.getTime();

  return isSameDay(date1, date2);
};

//if there is a providedDate creates a new Date with it otherwise creates a new Date
//useful for incoming data from api when you don't know if is null or not
export const createNewDate = (providedDate) =>
  new Date(providedDate || Date.now());

//returns the age for a provided birthdate
export const getAge = (birthDate) => {
  // birthday is a date
  const ageDifMs = Date.now() - birthDate.getTime();
  const ageDate = new Date(ageDifMs); // miliseconds from epoch
  return Math.abs(ageDate.getUTCFullYear() - 1970);
};

//add 0 to time string
export const addZero = (providedTime) => {
  const hours = providedTime.getHours();
  const minutes = providedTime.getMinutes();

  return `${`0${hours}`.slice(-2)}:${`0${minutes}`.slice(-2)}`;
};

export const calculateDaysBetweenRange = (dateA, dateB) =>
  differenceInCalendarDays(dateB, dateA);

export const addDays = (date, days) => addDaysFns(date || new Date(), days);

export const getMonday = (currentDate) =>
  startOfWeek(currentDate || new Date(), { weekStartsOn: 1 });

export const getFriday = (currentDate) => addDays(getMonday(currentDate), 4);
export const roundToHalfHour = (providedTime) => {
  let hours = providedTime.getHours();
  let minutes = providedTime.getMinutes();

  hours = minutes >= 45 ? hours + 1 : hours; //round to next hour if minutes are more than 45
  minutes = (Math.round(minutes / 30) * 30) % 60; //round minutes to multiple of 30

  return { hours, minutes };
};

export const isAroundTime = (providedTime, offset) =>
  Math.abs(providedTime.getTime() - Date.now()) < offset * 60 * 1000;

export const getPreviousDate = (providedDate) =>
  new Date(new Date(providedDate).getTime() - DAY_IN_MS);

export const getNextDate = (providedDate) =>
  new Date(new Date(providedDate).getTime() + DAY_IN_MS);

/**
 * Function accepts a date or a string and returns a formatted localeDateString:
 *
 * @param {[String, Date]} providedDate - Date or String. The date param that can be a string that will be parsed to a Date if possible.
 *
 * @returns {String}
 */

export const formatListItemDate = (providedDate, dateTime = false) => {
  const date = providedDate ? new Date(providedDate) : null;

  if (!date || isNaN(date.getTime())) return ''; //return empty string if invalid Date
  //shows both
  if (dateTime)
    return date.toLocaleDateString(culture, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  if (TODAY.getTime() - date.getTime() > DAY_IN_MS)
    //complete date without time
    return date.toLocaleDateString(culture, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  //only time
  return addZero(date);
};

// A period runs from the start of its first day to the end of its last, whatever the time is now.
const dayRange = (first, last) => [startOfDay(first), endOfDay(last)];

export const getTodayRange = () => {
  const today = new Date();
  return dayRange(today, today);
};

export const getYesterdayRange = () => {
  const yesterday = subDays(new Date(), 1);
  return dayRange(yesterday, yesterday);
};

// Work week: Monday to Friday
export const getThisWeekRange = () => {
  const monday = getMonday();
  return dayRange(monday, addDaysFns(monday, 4));
};

export const getLastWeekRange = () => {
  const monday = subDays(getMonday(), 7);
  return dayRange(monday, addDaysFns(monday, 4));
};

export const getThisMonthRange = () => {
  const now = new Date();
  return dayRange(startOfMonth(now), endOfMonth(now));
};

// The most recent occurrence of the month: a month still ahead this year is last year's.
export const getMonthRange = (monthNumber) => {
  const now = new Date();
  // monthNumber is 1-12, but Date months are 0-11
  const monthIndex = monthNumber - 1;
  const year = now.getFullYear() - (monthIndex > now.getMonth() ? 1 : 0);
  const first = new Date(year, monthIndex, 1);

  return dayRange(first, endOfMonth(first));
};

// The [start, end] of a report period: a named period, a month number (1-12), or `start` and `end`
// as given for 'range' and anything unnamed.
export const getPeriodRange = (per, start, end) => {
  if (typeof per === 'number') return getMonthRange(per);
  if (per === 'month') return getThisMonthRange();
  if (per === 'today') return getTodayRange();
  if (per === 'yesterday') return getYesterdayRange();
  if (per === 'week') return getThisWeekRange();
  if (per === 'lastWeek') return getLastWeekRange();
  return [start, end];
};

export const formatPayloadDate = (providedDate) => {
  if (!providedDate) return null;
  const date = new Date(providedDate);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
};
